import { RenderElementProps } from 'slate-react';

import DOMPurify from 'dompurify';
import { BaseText, Text } from 'slate';

import { CustomElement } from '../../common/TextArea/constants.js';
import SafeLink from '../../common/TextArea/SafeLink.jsx';
import {
  CustomSlateNode,
  extendedNodeTypes,
  getCssByTextStyle,
  stylesAttributes,
  type CustomLeaf,
} from '../../common/TextArea/utils.jsx';
import Typography from '../Typography/Typography.jsx';

const nodeTypes = {
  style_normal: 'p',
  style_heading1: 'h1',
  style_heading2: 'h2',
  'numbered-list': 'ol',
  'bulleted-list': 'ul',
};

export const renderEditorElement = ({
  attributes,
  children,
  element,
}: RenderElementProps) => {
  const customElement = element as CustomElement;

  const styledChildren = (
    <Typography sx={getCssByTextStyle(customElement.type)}>
      {children}
    </Typography>
  );

  if (customElement.type === 'placeholder') {
    return <span>{children}</span>;
  }

  switch (customElement.type) {
    case 'block-quote':
      return <blockquote {...attributes}>{styledChildren}</blockquote>;
    case 'bulleted-list':
      return <ul {...attributes}>{styledChildren}</ul>;
    case 'list-item':
      return <li {...attributes}>{styledChildren}</li>;
    case 'numbered-list':
      return <ol {...attributes}>{styledChildren}</ol>;
    default:
      return <p {...attributes}>{styledChildren}</p>;
  }
};

export const renderEditorElementExtended = ({
  attributes,
  children,
  element,
}: RenderElementProps) => {
  const customElement = element as CustomElement;

  const styledChildren = (
    <Typography sx={getCssByTextStyle(customElement.type)}>
      {children}
    </Typography>
  );

  if (customElement.type === 'placeholder') {
    return <span>{children}</span>;
  }

  switch (customElement.type) {
    case 'block-quote':
      return <blockquote {...attributes}>{styledChildren}</blockquote>;
    case 'bulleted-list':
      return <ul {...attributes}>{styledChildren}</ul>;
    case 'heading-one':
      return <h1 {...attributes}>{children}</h1>;
    case 'heading-two':
      return <h2 {...attributes}>{children}</h2>;
    case 'heading-three':
      return <h3 {...attributes}>{children}</h3>;
    case 'heading-four':
      return <h4 {...attributes}>{children}</h4>;
    case 'heading-five':
      return <h5 {...attributes}>{children}</h5>;
    case 'heading-six':
      return <h6 {...attributes}>{children}</h6>;
    case 'list-item':
      return <li {...attributes}>{styledChildren}</li>;
    case 'numbered-list':
      return <ol {...attributes}>{styledChildren}</ol>;
    case 'link':
      return (
        <SafeLink href={customElement.url ?? ''} attributes={attributes}>
          {children}
        </SafeLink>
      );
    default:
      return <p {...attributes}>{styledChildren}</p>;
  }
};

export const editorControlButtons = [
  {
    role: 'markButton',
    value: 'bold',
    icon: 'TextBold' as const,
  },
  {
    role: 'markButton',
    value: 'italic',
    icon: 'TextItalic' as const,
  },
  {
    role: 'markButton',
    value: 'underline',
    icon: 'TextUnderline' as const,
  },
  {
    role: 'markButton',
    value: 'linethrough',
    icon: 'TextStrikethrough' as const,
  },
  {
    role: 'blockButton',
    value: 'bulleted-list',
    icon: 'ListBulleted' as const,
  },

  {
    role: 'blockButton',
    value: 'numbered-list',
    icon: 'ListNumbered' as const,
  },
];

const serializeTextNode = (node: CustomSlateNode, type?: string) => {
  const textNode = node as BaseText & CustomLeaf;
  let string = DOMPurify.sanitize(textNode.text);

  Object.keys(stylesAttributes).forEach((style) => {
    const isAttribute = textNode[style as keyof CustomLeaf];

    if (isAttribute) {
      const assignedStyle =
        stylesAttributes[style as keyof typeof stylesAttributes];
      string = `<${assignedStyle}>${string}</${assignedStyle}>`;
    }
  });

  return type?.includes('list') ? `<li>${string}</li>` : string;
};

const serializeElement = (node: CustomSlateNode) => {
  const element = node as CustomElement;
  const children = element.children
    .map((n) => serializeSlateToHtml(n as CustomSlateNode, node.type))
    .join('');

  const tag =
    nodeTypes[(element as CustomSlateNode).type as keyof typeof nodeTypes];

  return tag ? `<${tag}>${children}</${tag}>` : children;
};

const serializeTextNodeNew = (node: CustomSlateNode, type?: string) => {
  const textNode = node as BaseText & CustomLeaf;
  let string = DOMPurify.sanitize(textNode.text);
  // Replace newline characters with <br /> tags to preserve line breaks
  string = string.replaceAll('\n', '<br />');

  // Loop through each style attribute and wrap the text in the corresponding HTML tag
  Object.keys(stylesAttributes).forEach((style) => {
    const isAttribute = textNode[style as keyof CustomLeaf];

    if (isAttribute) {
      const assignedStyle =
        stylesAttributes[style as keyof typeof stylesAttributes];
      string = `<${assignedStyle}>${string}</${assignedStyle}>`;
    }
  });
  // If the type includes 'list', wrap the text in <li> tags
  return type?.includes('list') ? `<li>${string}</li>` : string;
};

const serializeElementNew = (node: CustomSlateNode) => {
  const element = node as CustomElement;
  const children = element.children
    .map((n) => serializeSlateToHtmlNew(n as CustomSlateNode, node.type))
    .join('');

  // prevent adding empty tags to editor
  if (!children) {
    return '';
  }

  const tag =
    extendedNodeTypes[
      (element as CustomSlateNode).type as keyof typeof extendedNodeTypes
    ];

  return tag ? `<${tag}>${children}</${tag}>` : children;
};

export const serializeSlateToHtml = (
  node: CustomSlateNode,
  type?: string,
): string => {
  if (Text.isText(node)) {
    return serializeTextNode(node, type);
  }

  return serializeElement(node);
};

export const serializeSlateToHtmlNew = (
  node: CustomSlateNode,
  type?: string,
): string => {
  if (Text.isText(node)) {
    return serializeTextNodeNew(node, type);
  }

  return serializeElementNew(node);
};
